import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { fetchNotifications, selectNotifications, selectNotificationsLoading, markNotificationAsRead } from '../../redux/notificationsSlice';
// import Header from '../../components/header/header';
import Cover from '../../components/cover/cover';
import { getContainerClass } from '../../components/navigation/navigation';
import { FaCalendarPlus, FaCalendarTimes, FaCalendarCheck, FaSearch, FaCalendarAlt } from 'react-icons/fa';
import { MdNotifications } from 'react-icons/md';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import './NotificationsPage.css';
import moment from 'moment';

const NotificationsPage = () => {
  const dispatch = useDispatch();
  const notifications = useSelector(selectNotifications);
  const loading = useSelector(selectNotificationsLoading);
  const location = useLocation();
  const breadcrumb = ["Home", "Notifications"];
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const itemsPerPage = 10;

  console.log('Notifications:', notifications);

  useEffect(() => {
    loadNotifications(1, true); // true means reset list
  }, [dispatch]);

  const loadNotifications = async (page, reset = false) => {
    try {
      const response = await dispatch(fetchNotifications({ 
        page, 
        limit: itemsPerPage 
      })).unwrap();
      
      // If we get fewer items than the limit, we've reached the end
      setHasMore(response?.notifications?.length === itemsPerPage);
      
    } catch (error) {
      console.error('Error loading notifications:', error);
      setHasMore(false);
    }
  };

  const handlePageChange = (direction) => {
    if (direction === 'next' && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      loadNotifications(nextPage);
    } else if (direction === 'prev' && currentPage > 1) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);
      loadNotifications(prevPage);
    }
  };

  const renderPagination = () => {
    return (
      <div className="pagination-container">
        <button
          className="pagination-button"
          onClick={() => handlePageChange('prev')}
          disabled={currentPage === 1}
        >
          <IoChevronBack /> Previous
        </button>

        <span className="pagination-info">
          Page {currentPage}
        </span>

        <button
          className="pagination-button"
          onClick={() => handlePageChange('next')}
          disabled={!hasMore}
        >
          Next <IoChevronForward />
        </button>
      </div>
    );
  };

  const getNotificationIcon = (notification) => {
    const title = notification.title?.toLowerCase() || '';
    const type = notification.type?.toLowerCase() || '';
    
    if (title.includes('cancelled') || notification.entity_type === 'cancelled') {
      return { icon: FaCalendarTimes, bgColor: '#dc3545', iconColor: '#fff' }; // Red for cancellations
    }
    
    if (type === 'multi') {
      return { icon: FaCalendarPlus, bgColor: '#6f42c1', iconColor: '#fff' }; // Purple for multiple bookings
    }
    
    if (title.includes('booked') || notification.entity_type === 'Booking') {
      return { icon: FaCalendarAlt, bgColor: '#36b000', iconColor: '#fff' }; // Green for bookings
    }
    
    // Default icon
    return { icon: MdNotifications, bgColor: '#74c0fc', iconColor: '#fff' }; // Blue default
  };

  const filteredNotifications = notifications.filter(notification => 
    notification.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    notification.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleNotificationClick = (notification) => {
    if (notification.status === 'unread') {
      dispatch(markNotificationAsRead(notification._id));
    }
    // Could add specific navigation based on notification type here if needed
  };

  const renderGroupHeader = (item) => (
    item.showDateHeader ? (
      <div className="notif-date-header">{item.formattedDate}</div>
    ) : null
  );

  const renderSkeletonNotifications = () => (
    <div className="notif-list">
      <div className="notif-date-header skeleton-date"></div>
      {[1, 2, 3, 4, 5].map((index) => (
        <div key={index} className="notif-item skeleton-notif">
          <div className="skeleton-icon-container">
            <div className="skeleton-icon"></div>
          </div>
          <div className="skeleton-content">
            <div className="skeleton-title-row">
              <div className="skeleton-title"></div>
              <div className="skeleton-time"></div>
            </div>
            <div className="skeleton-desc"></div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      {/* <Header /> */}
      <div className={getContainerClass(location.pathname)}>
        <Cover breadcrumb={breadcrumb} page="notifications" />
        <div className="notifications-page">
          <div className="notifications-controls">
            <div className="search-container">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search notifications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          {loading ? (
            renderSkeletonNotifications()
          ) : filteredNotifications.length === 0 ? (
            <div className="notif-empty">
              {searchTerm ? 'No notifications found matching your search' : 'No notifications yet'}
            </div>
          ) : (
            <div className="notif-table">
              <div className="notif-list">
                {filteredNotifications.map((n) => {
                  const iconConfig = getNotificationIcon(n);
                  const IconComponent = iconConfig.icon;
                  
                  return (
                    <React.Fragment key={n._id}>
                      {renderGroupHeader(n)}
                      <div 
                        className={`notif-item ${n.status === 'unread' ? 'unread' : ''}`}
                        onClick={() => handleNotificationClick(n)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div 
                          className="notif-icon-container" 
                          style={{ backgroundColor: iconConfig.bgColor }}
                        >
                          <IconComponent 
                            className="notif-icon" 
                            style={{ color: iconConfig.iconColor }}
                          />
                        </div>
                        <div className="notif-content">
                          <div className="notif-title-row">
                            <div className="notif-title">
                              {n.title}
                              {n.entity_type === 'Booking' && (n.actions?.BookingId || n.actions?.BookingID || n.actions.bookingID) && (
                                <span className="booking-id-tag">Booking #{n.actions.BookingId ?? n.actions.BookingID ?? n.actions.bookingID}</span>
                              )}
                            </div>
                            <div className="notif-time">{moment(n.createdAt).format('HH:mm')}</div>
                          </div>
                          <div className="notif-desc">{n.description}</div>
                        </div>
                        {n.status === 'unread' && <div className="notification-dot-page"></div>}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
              <div className="notif-table-footer">
                <div className="pagination-info">
                  Page {currentPage}
                </div>
                <div className="table-pagination">
                  <button
                    className="table-pagination-button"
                    onClick={() => handlePageChange('prev')}
                    disabled={currentPage === 1}
                  >
                    <IoChevronBack /> Previous
                  </button>
                  <button
                    className="table-pagination-button"
                    onClick={() => handlePageChange('next')}
                    disabled={!hasMore}
                  >
                    Next <IoChevronForward />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationsPage; 